/* eslint-disable react/no-unescaped-entities */
import React, { useContext, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Brand, useSession } from '@lendi/lala-react';
import { Body } from '@lendi-ui/typography';
import Grid from '@lendi-ui/grid';
import { OptionType, Select } from '@lendi-ui/dropdown';
import { Radio } from '@lendi-ui/radio';
import { Input } from '@lendi-ui/text-input';
import { FlexWrap, RadioWrap, TooltipHelp, Wrap } from './index.style';
import Tooltip from '@lendi-ui/tooltip';
import getColour from '@lendi-ui/commons/colours';
import { PlatformPathname } from '@lendi/core-constants';
import { Button } from '../atom/ButtonWithMargin';
import { BoldHeading, DollarWithMargin } from '../atom';
import NumberFormat from 'react-number-format';
import { convertToPascalCase } from '../../utils/helpers';
import ErrorMessage from '../ErrorMessage';
import { ButtonType, CustomerType, EventCategory } from '@lendi/analytics-web';
import {
  BUTTON_CLICKED_EVENT,
  DROPDOWN_SELECTED_EVENT,
  FORM_ELEMENT_ANSWERED_EVENT,
  RADIOBUTTON_SELECTED_EVENT,
} from '../../utils';
import {
  createLead,
  Origin,
  PropertyUse,
  StampDutyPropertyState,
  StampDutyPropertyType,
} from '@lendi/lendigroup-leads-library';
import { StampDutyCalculatorLead } from '../../interfaces';
import { LDContext } from '../../utils/launchdarkly';

export const propertyTypes = [
  {
    label: 'Newly built / Off the plan',
    value: StampDutyPropertyType.NewHome,
  },
  {
    label: 'Established home',
    value: StampDutyPropertyType.EstablishedHome,
  },
  {
    label: 'Vacant land to build on',
    value: StampDutyPropertyType.VacantLand,
  },
];

const propertyUseOptions = [
  {
    label: 'Owner occupied',
    value: PropertyUse.OwnerOccupied,
  },
  {
    label: 'Investment',
    value: PropertyUse.Investment,
  },
];

export enum CurrentSituation {
  FirstHomeBuyer = 'First home buyer',
  ForeignInvestor = 'Foreign investor',
}

const currentSituationOptions = [
  {
    label: 'First home buyer',
    value: CurrentSituation.FirstHomeBuyer,
  },
  {
    label: 'Foreign investor',
    value: CurrentSituation.ForeignInvestor,
  },
  {
    label: 'Neither',
    value: 'Neither',
  },
];
interface StampDutyQuestionFormData {
  propertyType: StampDutyPropertyType;
  propertyState: StampDutyPropertyState;
  propertyUse: PropertyUse;
  propertyValue: number;
  estimatedDeposit: number;
  currentSituation: string;
}

const mapFormDataToLeadsData = (formData: StampDutyQuestionFormData): Partial<StampDutyCalculatorLead> => {
  const { estimatedDeposit, propertyValue, currentSituation, propertyState, propertyType, propertyUse } = formData;

  return {
    accountOrigin: `${window.location.protocol}//${window.location.host}${PlatformPathname.STAMP_DUTY_PATHNAME}${window.location.search}`,
    data: {
      deposit: estimatedDeposit,
      securityValue: propertyValue,
      isFirstHomeBuyer: currentSituation === CurrentSituation.FirstHomeBuyer,
      isForeignInvestor: currentSituation === CurrentSituation.ForeignInvestor,
      propertyState,
      propertyType,
      propertyUse,
    },
  };
};

const mapLeadsDataToFormData = (leadsData: StampDutyCalculatorLead): StampDutyQuestionFormData => {
  const { deposit, securityValue, isFirstHomeBuyer, isForeignInvestor, propertyState, propertyType, propertyUse } =
    leadsData?.data || {};

  let currentSituation = 'Neither';
  if (isFirstHomeBuyer && !isForeignInvestor) {
    currentSituation = CurrentSituation.FirstHomeBuyer;
  } else if (!isFirstHomeBuyer && isForeignInvestor) {
    currentSituation = CurrentSituation.ForeignInvestor;
  }

  return {
    estimatedDeposit: deposit,
    propertyValue: securityValue,
    currentSituation,
    propertyState,
    propertyType,
    propertyUse,
  };
};

const StampDutyQuestionContent: React.FC = () => {
  if (global.window === undefined) return null;
  const L3 = createLead(Origin.StampDutyCalculator);
  const existingLeadsData = L3.get();
  const { brand } = useSession();
  const isAussie = brand === Brand.Aussie;
  const {
    control,
    handleSubmit,
    formState: { isValid, errors },
  } = useForm({ mode: 'all', reValidateMode: 'onSubmit', defaultValues: mapLeadsDataToFormData(existingLeadsData) });

  const { analytics } = useContext(LDContext);
  useEffect(() => {
    analytics?.trackPage('Stamp Duty Calculator', { event_category: EventCategory.STAMP_DUTY_CALCULATOR });
  }, []);

  const fireEvents = async (leadsData: any) => {
    const propertyTypeObj = propertyTypes.find((type) => type.value === leadsData.propertyType);
    const propertyUseObj = propertyUseOptions.find((use) => use.value === leadsData.propertyUse);

    analytics?.trackEvent({
      event_name: DROPDOWN_SELECTED_EVENT,
      category: EventCategory.STAMP_DUTY_CALCULATOR,
      title: 'Property Type',
      selection: convertToPascalCase(propertyTypeObj!.label),
      customerType: CustomerType.CUSTOMER,
      path: window.location.pathname,
    });
    analytics?.trackEvent({
      event_name: DROPDOWN_SELECTED_EVENT,
      category: EventCategory.STAMP_DUTY_CALCULATOR,
      title: 'State',
      selection: convertToPascalCase(leadsData.propertyState),
      customerType: CustomerType.CUSTOMER,
      path: window.location.pathname,
    });
    analytics?.trackEvent({
      event_name: RADIOBUTTON_SELECTED_EVENT,
      category: EventCategory.STAMP_DUTY_CALCULATOR,
      title: 'Property Use',
      selection: convertToPascalCase(propertyUseObj!.label),
      customerType: CustomerType.CUSTOMER,
      path: window.location.pathname,
    });
    analytics?.trackEvent({
      event_name: FORM_ELEMENT_ANSWERED_EVENT,
      category: EventCategory.STAMP_DUTY_CALCULATOR,
      question: 'Expected Property Value',
      answer: leadsData.propertyValue,
      customerType: CustomerType.CUSTOMER,
      path: window.location.pathname,
    });
    analytics?.trackEvent({
      event_name: FORM_ELEMENT_ANSWERED_EVENT,
      category: EventCategory.STAMP_DUTY_CALCULATOR,
      question: 'Estimated Deposit',
      answer: leadsData.estimatedDeposit,
      customerType: CustomerType.CUSTOMER,
      path: window.location.pathname,
    });
    analytics?.trackEvent({
      event_name: RADIOBUTTON_SELECTED_EVENT,
      category: EventCategory.STAMP_DUTY_CALCULATOR,
      title: 'Current Situation',
      selection: convertToPascalCase(leadsData.currentSituation),
      customerType: CustomerType.CUSTOMER,
      path: window.location.pathname,
    });
    await analytics?.trackEvent(
      {
        event_name: BUTTON_CLICKED_EVENT,
        category: EventCategory.STAMP_DUTY_CALCULATOR,
        text: 'Next',
        buttonType: ButtonType.PRIMARY,
        customerType: CustomerType.CUSTOMER,
        path: window.location.pathname,
      },
      true
    );
  };
  const onSubmit = async (data: StampDutyQuestionFormData) => {
    const leadsData = mapFormDataToLeadsData(data);

    if (leadsData) {
      const L3 = createLead(Origin.StampDutyCalculator);
      L3.set(leadsData);
      await fireEvents(mapLeadsDataToFormData(leadsData as StampDutyCalculatorLead));
    }
    const searchParams = window.location.search;
    window.location.assign(`./result${searchParams}`);
  };

  const requiredErrMsg = (field?: string): string => {
    if (!field) return 'Please select an option';
    return `Please enter a ${field === 'propertyValue' ? 'property value' : 'deposit amount'}`;
  };

  const minErrMsg = 'Property value must be equal to or greater than $1000';
  const maxErrMsg = 'Property value must be less than $100 million';

  return (
    <Grid halign="center">
      <Grid.Unit size={{ xs: 1, md: 1 / 2 }}>
        <Wrap>
          <BoldHeading
            size="md"
            as="h1"
            color="shade.700"
            mt="sm"
            mb="sm"
            align="center"
            data-testid="stamp-duty-heading"
          >
            Stamp duty calculator
          </BoldHeading>

          <Body size="sm" color="shade.700" align="center" mb="lg">
            Stamp duty is a one-off government tax you'll need to pay when buying a property. It's one of the biggest
            upfront costs to purchasing so it's important to factor it into your budget.
          </Body>
          <form data-testid="stamp-duty-form" onSubmit={handleSubmit(onSubmit)}>
            <Body size="md" color="shade.700" align="left" mb="xxs">
              Property type
            </Body>
            <Controller
              name="propertyType"
              control={control}
              rules={{ required: requiredErrMsg() }}
              render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                <Select
                  data-testid="stamp-duty-form-property-type"
                  size="md"
                  options={propertyTypes}
                  placeholder="Please select"
                  isFullWidth
                  hideSelectedOptions={false}
                  onChangeItem={(option: OptionType | undefined | null) => onChange(option?.value)}
                  onBlur={onBlur}
                  value={propertyTypes.find((propertyType) => propertyType.value === value)}
                  isError={Boolean(error)}
                  aria-label="property-type-select-input"
                />
              )}
            />
            <ErrorMessage field="propertyType" errors={errors} />

            <Body size="md" color="shade.700" align="left" mt="md" mb="xxs">
              State
            </Body>
            <Controller
              name="propertyState"
              control={control}
              rules={{ required: requiredErrMsg() }}
              render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                <Select
                  data-testid="stamp-duty-form-property-state"
                  size="md"
                  options={Object.values(StampDutyPropertyState).map((s) => ({ label: s, value: s }))}
                  placeholder="Please select"
                  isFullWidth
                  hideSelectedOptions={false}
                  onChangeItem={(option: OptionType | undefined | null) => onChange(option?.value)}
                  onBlur={onBlur}
                  isError={Boolean(error)}
                  value={Object.values(StampDutyPropertyState)
                    .map((s) => ({ label: s, value: s }))
                    .find((s) => s.value === value)}
                  aria-label="state-select-input"
                />
              )}
            />
            <Body size="xs" color="shade.700" align="left" mt="xxs" mb="md">
              Different states calculate stamp duty differently
            </Body>
            <ErrorMessage field="propertyState" errors={errors} />

            <Body size="md" color="shade.700" align="left">
              Property use
            </Body>
            <Controller
              data-testid="stamp-duty-form-property-use"
              name="propertyUse"
              control={control}
              rules={{ required: requiredErrMsg() }}
              render={({ field: { onChange, value } }) => (
                <RadioWrap>
                  {propertyUseOptions.map((propertyUse) => {
                    return (
                      <Radio
                        data-testid="property-use-radio"
                        key={propertyUse.label}
                        label={propertyUse.label}
                        value={propertyUse.value}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
                        isChecked={value === propertyUse.value}
                      />
                    );
                  })}
                </RadioWrap>
              )}
            />
            <ErrorMessage field="propertyUse" errors={errors} />

            <Body size="md" color="shade.700" align="left" mt="md" mb="xxs">
              Expected property value
            </Body>
            <Controller
              name="propertyValue"
              control={control}
              rules={{
                required: requiredErrMsg('propertyValue'),
                min: {
                  value: 1000,
                  message: minErrMsg,
                },
                max: {
                  value: 99999999,
                  message: maxErrMsg,
                },
              }}
              render={({ field: { onChange, value, onBlur } }) => (
                <NumberFormat
                  data-testid="stamp-duty-form-property-value"
                  decimalScale={0}
                  thousandSeparator={true}
                  customInput={Input}
                  isFullWidth
                  isError={errors.propertyValue}
                  before={<DollarWithMargin color={getColour('fill', 'accent')} height="1.5rem" width="1.5rem" />}
                  aria-label="property-value-input"
                  onValueChange={(value) => onChange(value.floatValue)}
                  value={value}
                  onBlur={onBlur}
                />
              )}
            />
            <ErrorMessage field="propertyValue" errors={errors} />

            <Body size="md" color="shade.700" align="left" mt="md" mb="xxs">
              Estimated deposit
            </Body>
            <Controller
              name="estimatedDeposit"
              control={control}
              rules={{
                required: requiredErrMsg('estimatedDeposit'),
                min: {
                  value: 1000,
                  message: minErrMsg,
                },
                max: {
                  value: 99999999,
                  message: maxErrMsg,
                },
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <NumberFormat
                  data-testid="stamp-duty-form-estimated-deposit"
                  decimalScale={0}
                  thousandSeparator={true}
                  customInput={Input}
                  isFullWidth
                  isError={errors.estimatedDeposit}
                  before={<DollarWithMargin color={getColour('fill', 'accent')} height="1.5rem" width="1.5rem" />}
                  aria-label="estimated-deposit-input"
                  onValueChange={(value) => onChange(value.floatValue)}
                  value={value}
                  onBlur={onBlur}
                />
              )}
            />
            <ErrorMessage field="estimatedDeposit" errors={errors} />

            <Body size="md" color="shade.700" align="left" mt="md">
              Current situation
            </Body>
            <Controller
              name="currentSituation"
              defaultValue="Neither"
              control={control}
              render={({ field: { onChange, value } }) => (
                <RadioWrap>
                  {currentSituationOptions.map((situation) => {
                    const radioElm = (
                      <Radio
                        data-testid="current-situation-radio"
                        key={situation.label}
                        label={situation.label}
                        value={situation.value}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
                        isChecked={value === situation.value}
                      />
                    );

                    if (situation.label === 'First home buyer') {
                      return (
                        <FlexWrap key={situation.label}>
                          {radioElm}
                          <Tooltip
                            content={`First home buyers are Australian citizens or permanent residents of Australia, who are over 18 years old, and looking to purchase their first property in Australia. For more details please book an appointment to speak to ${
                              isAussie ? 'an Aussie Broker' : 'a Lendi specialist'
                            }.`}
                            position="top"
                          >
                            <TooltipHelp color={getColour('fill', 'contrast')} height="20px" width="20px" />
                          </Tooltip>
                        </FlexWrap>
                      );
                    }
                    return radioElm;
                  })}
                </RadioWrap>
              )}
            />
            <Grid halign="center">
              <Grid.Unit size={{ xs: 1, md: 3 / 5 }}>
                <Button
                  data-testid="stamp-duty-form-submit-button"
                  mt="lg"
                  isDisabled={!isValid}
                  type="submit"
                  size="md"
                  variant="primary"
                  isFullWidth
                  aria-label="stamp-duty-next-button"
                >
                  NEXT
                </Button>
              </Grid.Unit>
            </Grid>
          </form>
        </Wrap>
      </Grid.Unit>
    </Grid>
  );
};

export default StampDutyQuestionContent;
